import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../design';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, Activity, TrendingUp, Zap, BellRing, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const yesterdayISO = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const LINE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#38bdf8'];

export default function UsageAudit() {
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(yesterdayISO());
  const [threshold, setThreshold] = useState('5');
  const [data, setData] = useState(null);

  // Trend
  const [trendDays, setTrendDays] = useState(30);
  const [trend, setTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(true);

  // Alert config + recent alerts
  const [cfg, setCfg] = useState({ threshold_usd: 25, enabled: true });
  const [savingCfg, setSavingCfg] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async (d, t) => {
    setLoading(true);
    try {
      const res = await governanceAPI.getUniversalCreditAudit(d, t);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load usage audit', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrend = useCallback(async (days) => {
    setTrendLoading(true);
    try {
      const res = await governanceAPI.getUniversalCreditTrend(days, 5);
      setTrend(res.data);
    } catch (e) {
      toast.error('Failed to load spend trend', { description: e?.response?.data?.detail || e.message });
    } finally {
      setTrendLoading(false);
    }
  }, []);

  const loadAlertMeta = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([
        governanceAPI.getLLMSpendAlertConfig(),
        governanceAPI.getLLMSpendAlerts(20),
      ]);
      setCfg(c.data);
      setAlerts(a.data.alerts || []);
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    load(date, threshold);
    loadTrend(trendDays);
    loadAlertMeta();
     
  }, []);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const res = await governanceAPI.setLLMSpendAlertConfig(Number(cfg.threshold_usd), cfg.enabled);
      setCfg(res.data);
      toast.success('Alert settings saved', {
        description: `Accounts over $${res.data.threshold_usd}/day will be flagged daily.`,
      });
    } catch (e) {
      toast.error('Could not save settings', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingCfg(false);
    }
  };

  const runCheckNow = async () => {
    try {
      const res = await governanceAPI.runLLMSpendAlertCheck();
      toast.success(`Check complete — ${res.data.alerts_created || 0} new alert(s)`, {
        description: `${res.data.date}: ${(res.data.flagged || []).length} account(s) over threshold.`,
      });
      await loadAlertMeta();
    } catch (e) {
      toast.error('Check failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const accounts = data?.by_account || [];
  const features = data?.by_feature || [];
  const flagged = data?.flagged_accounts || [];
  const maxSpend = accounts.length ? Math.max(...accounts.map((a) => a.billed_credits_usd || 0)) : 0;

  // Merge overall + per-top-account daily series into rows keyed by date.
  const trendChartData = (() => {
    if (!trend?.series) return [];
    const byDate = {};
    trend.series.forEach((s) => { byDate[s.date] = { date: s.date, Total: s.total_usd }; });
    (trend.top_accounts || []).forEach((a) => {
      (a.series || []).forEach((pt) => {
        if (byDate[pt.date]) byDate[pt.date][a.name] = pt.usd;
      });
    });
    return trend.series.map((s) => byDate[s.date]);
  })();

  return (
    <div className="space-y-6" data-testid="usage-audit-page">
      <PageHeader
        eyebrow="Cost Ops"
        title="Usage Audit"
        subtitle="Universal-key (LLM) credit consumed per account and per feature on a given day. Spot runaway or looping accounts before they burn the wallet."
        actions={
          <Button variant="outline" size="sm" onClick={() => load(date, threshold)} disabled={loading} data-testid="usage-audit-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label htmlFor="audit-date" className="gov-text-tertiary text-xs">Date (UTC)</Label>
          <Input id="audit-date" type="date" value={date} max={yesterdayISO()}
                 onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="usage-audit-date" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-threshold" className="gov-text-tertiary text-xs">Flag accounts over ($/day)</Label>
          <Input id="audit-threshold" type="number" min="0" step="0.5" value={threshold}
                 onChange={(e) => setThreshold(e.target.value)} className="w-36" data-testid="usage-audit-threshold" />
        </div>
        <Button onClick={() => load(date, threshold)} disabled={loading} data-testid="usage-audit-run">Run audit</Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { label: 'Total LLM spend', value: data ? usd(data.total_billed_credits_usd) : '—', icon: TrendingUp, testid: 'stat-spend' },
          { label: 'AI calls', value: data?.total_calls ?? '—', icon: Activity, testid: 'stat-calls' },
          { label: 'Accounts active', value: data?.account_count ?? '—', icon: Zap, testid: 'stat-accounts' },
          { label: 'Flagged', value: flagged.length, icon: AlertTriangle, testid: 'stat-flagged' },
        ].map((s) => (
          <Card key={s.label} className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 gov-text-tertiary text-xs uppercase tracking-wide">
                <s.icon className="h-3.5 w-3.5" />{s.label}
              </div>
              <div className="gov-text-primary mt-1 text-2xl font-semibold" data-testid={s.testid}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Flagged alert */}
      {flagged.length > 0 && (
        <Alert className="border-amber-500/40 bg-amber-500/10" data-testid="usage-audit-flag-alert">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertTitle>{flagged.length} account(s) over ${threshold}/day</AlertTitle>
          <AlertDescription>
            {flagged.map((f) => (
              <div key={f.tenant_id} className="mt-1 text-sm">
                <strong>{f.name || f.tenant_id}</strong> — {usd(f.billed_credits_usd)} across {f.calls} calls
                {f.top_feature && <> · mostly <Badge variant="outline" className="ml-1">{f.top_feature}</Badge> ({f.top_feature_calls} calls)</>}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* By account */}
        <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)] lg:col-span-2">
          <CardHeader>
            <CardTitle className="gov-text-primary text-lg">Spend by account</CardTitle>
            <CardDescription className="gov-text-secondary">Highest consumers first</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : accounts.length === 0 ? (
              <p className="gov-text-tertiary py-8 text-center text-sm" data-testid="usage-audit-empty">No LLM usage on this day.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="w-40">Share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((a, idx) => {
                    const isFlagged = flagged.some((f) => f.tenant_id === a.tenant_id);
                    const pct = maxSpend > 0 ? Math.round((a.billed_credits_usd / maxSpend) * 100) : 0;
                    return (
                      <TableRow key={a.tenant_id} data-testid={`usage-row-${a.tenant_id}`} className={isFlagged ? 'bg-amber-500/5' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {idx === 0 && <Badge className="bg-red-500/15 text-red-500 border-red-500/30">Top</Badge>}
                            <div>
                              <div className="gov-text-primary font-medium">{a.name || 'Unnamed'}</div>
                              <div className="gov-text-tertiary text-xs">{a.email || a.tenant_id}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="gov-text-secondary text-right">{a.calls}</TableCell>
                        <TableCell className="gov-text-primary text-right font-medium">{usd(a.billed_credits_usd)}</TableCell>
                        <TableCell>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--gov-bg-input)]">
                            <div className={`h-full rounded-full ${isFlagged ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* By feature */}
        <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]">
          <CardHeader>
            <CardTitle className="gov-text-primary text-lg">Spend by feature</CardTitle>
            <CardDescription className="gov-text-secondary">Where the credits went</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
            ) : features.length === 0 ? (
              <p className="gov-text-tertiary py-8 text-center text-sm">No data.</p>
            ) : (
              <div className="space-y-3">
                {features.map((f) => (
                  <div key={f.feature} className="flex items-center justify-between" data-testid={`feature-row-${f.feature}`}>
                    <div>
                      <div className="gov-text-primary text-sm font-medium">{f.feature}</div>
                      <div className="gov-text-tertiary text-xs">{f.calls} calls</div>
                    </div>
                    <div className="gov-text-primary text-sm font-medium">{usd(f.billed_credits_usd)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Spend trend */}
      <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid="usage-trend-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="gov-text-primary text-lg">Spend trend</CardTitle>
            <CardDescription className="gov-text-secondary">Daily LLM spend — total + top accounts</CardDescription>
          </div>
          <div className="flex gap-1">
            {[7, 30].map((d) => (
              <Button key={d} size="sm" variant={trendDays === d ? 'default' : 'outline'}
                      onClick={() => { setTrendDays(d); loadTrend(d); }} data-testid={`trend-range-${d}`}>
                {d}d
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {trendLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <div style={{ width: '100%', height: 280 }} data-testid="usage-trend-chart">
              <ResponsiveContainer>
                <LineChart data={trendChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gov-border)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--gov-text-tertiary)' }}
                         tickFormatter={(d) => String(d).slice(5)} minTickGap={20} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--gov-text-tertiary)' }}
                         tickFormatter={(v) => `$${v}`} width={48} />
                  <Tooltip contentStyle={{ background: 'var(--gov-bg-surface)', border: '1px solid var(--gov-border)', borderRadius: 8, fontSize: 12 }}
                           formatter={(v) => usd(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Total" stroke="#94a3b8" strokeWidth={2.5} dot={false} />
                  {(trend?.top_accounts || []).map((a, i) => (
                    <Line key={a.tenant_id} type="monotone" dataKey={a.name}
                          stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={1.5} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alert settings + recent alerts */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)]" data-testid="alert-config-card">
          <CardHeader>
            <CardTitle className="gov-text-primary flex items-center gap-2 text-lg">
              <BellRing className="h-4 w-4" /> Daily alert
            </CardTitle>
            <CardDescription className="gov-text-secondary">
              Auto-flag any account that spends over this each day (checked every 24h).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="cfg-enabled" className="gov-text-secondary text-sm">Enabled</Label>
              <Switch id="cfg-enabled" checked={cfg.enabled}
                      onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))}
                      data-testid="alert-enabled-switch" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cfg-threshold" className="gov-text-tertiary text-xs">Threshold ($/day/account)</Label>
              <Input id="cfg-threshold" type="number" min="0" step="1" value={cfg.threshold_usd}
                     onChange={(e) => setCfg((c) => ({ ...c, threshold_usd: e.target.value }))}
                     data-testid="alert-threshold-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveCfg} disabled={savingCfg} data-testid="alert-save-btn">
                <Save className="mr-2 h-4 w-4" /> {savingCfg ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" onClick={runCheckNow} data-testid="alert-run-now-btn">Run check now</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[var(--gov-bg-surface)] border-[var(--gov-border)] lg:col-span-2" data-testid="recent-alerts-card">
          <CardHeader>
            <CardTitle className="gov-text-primary text-lg">Recent alerts</CardTitle>
            <CardDescription className="gov-text-secondary">Accounts that crossed the daily threshold</CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <p className="gov-text-tertiary py-8 text-center text-sm" data-testid="alerts-empty">No alerts yet.</p>
            ) : (
              <div className="space-y-2">
                {alerts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-[var(--gov-border)] bg-[var(--gov-bg-input)] px-3 py-2"
                       data-testid={`alert-item-${i}`}>
                    <div>
                      <div className="gov-text-primary text-sm font-medium">{a.tenant_name || a.tenant_id}</div>
                      <div className="gov-text-tertiary text-xs">{a.day_key} · {a.calls} calls</div>
                    </div>
                    <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30">{usd(a.spend_usd)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
